import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FileUpload from '../../../components/FileUpload';
import { Card, CardBody, CardHeader, CardTitle, Button, Row, Col } from 'react-bootstrap';
import supplierRepository from '../../../services/repositories/supplier.repository';
import '../../../styles/products.scss';

const AddSupplier = ({ isEdit = false, supplierId = null }) => {
  const navigate = useNavigate();
  const [supplierData, setSupplierData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    type: 'local',
    status: 'active',
    image: null,
    imageUrl: null,
  });

  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [supplierLoaded, setSupplierLoaded] = useState(false);

  useEffect(() => {
    if (isEdit && supplierId) {
      fetchSupplierData();
    } else {
      setSupplierLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, supplierId]);

  const fetchSupplierData = async () => {
    try {
      setFetching(true);
      setError(null);

      const response = await supplierRepository.getById(supplierId);
      const supplier = response.data || response;

      if (!supplier || !supplier.id) {
        throw new Error('Supplier data not found');
      }

      const { buildImageUrl } = await import('../../../utils/imageUrl');
      const imageUrl = buildImageUrl(supplier.image);

      const formattedData = {
        name: supplier.name || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        type: supplier.type || 'local',
        status: supplier.status || 'active',
        image: supplier.image || null,
        imageUrl: imageUrl,
      };

      setSupplierData(formattedData);
      setSupplierLoaded(true);
    } catch (err) {
      console.error('Error fetching supplier:', err);
      setError(err.message || 'Error loading supplier data');
      setSupplierLoaded(true);
    } finally {
      setFetching(false);
    }
  };

  const handleImageChange = (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      file.preview = URL.createObjectURL(file);
      setSupplierData(prev => ({
        ...prev,
        image: file,
        imageUrl: file.preview
      }));
    }
  };

  const handleRemoveImage = () => {
    setSupplierData(prev => ({
      ...prev,
      image: null,
      imageUrl: null
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSupplierData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        name: supplierData.name,
        email: supplierData.email,
        phone: supplierData.phone,
        address: supplierData.address,
        type: supplierData.type,
        status: supplierData.status,
      };

      // Only include image if it's a new file
      if (supplierData.image && supplierData.image instanceof File) {
        submitData.image = supplierData.image;
      }

      if (isEdit && supplierId) {
        await supplierRepository.update(supplierId, submitData);
      } else {
        await supplierRepository.create(submitData);
      }

      navigate('/admin/suppliers/supplier-list');
    } catch (err) {
      console.error('Error saving supplier:', err);

      // Handle validation errors specifically
      if (err.response?.data?.errors) {
        const validationErrors = Object.values(err.response.data.errors).flat().join(', ');
        setError(`Erreurs de validation: ${validationErrors}`);
      } else {
        setError(err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/suppliers/supplier-list');
  };

  if (fetching || !supplierLoaded) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p className="mt-3">Chargement des données du fournisseur...</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>
          {isEdit ? 'Modifier le Fournisseur' : 'Nouveau Fournisseur'}
        </CardTitle>
      </CardHeader>
      <CardBody>
        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <Row>
            <Col lg={6}>
              <div className="mb-2">
                <label htmlFor="name" className="form-label">
                  Nom <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-control"
                  placeholder="Nom du fournisseur"
                  value={supplierData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-2">
                <label htmlFor="email" className="form-label">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-control"
                  placeholder="Email"
                  value={supplierData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <div className="mb-2">
                <label htmlFor="phone" className="form-label">
                  Téléphone
                </label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  className="form-control"
                  placeholder="Téléphone"
                  value={supplierData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-2">
                <label htmlFor="type" className="form-label">
                  Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  id="type"
                  name="type"
                  value={supplierData.type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="local">Local</option>
                  <option value="international">International</option>
                </select>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <div className="mb-2">
                <label htmlFor="status" className="form-label">
                  Statut <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  id="status"
                  name="status"
                  value={supplierData.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </select>
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-2">
                <label htmlFor="address" className="form-label">
                  Adresse
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  className="form-control"
                  placeholder="Adresse"
                  value={supplierData.address}
                  onChange={handleInputChange}
                />
              </div>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={12}>
              <div className="mb-3">
                <label className="form-label">
                  Image
                </label>
                <div className="border border-2 border-dashed rounded p-3" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8f9fa' }}>
                  {supplierData.imageUrl ? (
                    <div className="text-center">
                      <div className="position-relative d-inline-block">
                        <img
                          src={supplierData.imageUrl}
                          alt="Image"
                          className="img-fluid rounded border"
                          style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'cover' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 m-2"
                          onClick={handleRemoveImage}
                          style={{ zIndex: 10 }}
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <FileUpload
                      title=""
                      onFilesChange={handleImageChange}
                      maxFiles={1}
                    />
                  )}
                </div>
              </div>
            </Col>
          </Row>

          <div className="p-3 bg-light mb-3 rounded">
            <Row className="justify-content-end g-2">
              <Col lg={2}>
                <Button
                  variant="dark"
                  className="w-100"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Annuler
                </Button>
              </Col>
              <Col lg={2}>
                <Button
                  variant="primary"
                  className="w-100"
                  type="submit"
                  disabled={loading}
                  style={{ background: 'linear-gradient(135deg, #c5a05e 0%, #b8934a 100%)', border: 'none' }}
                >
                  {loading ? 'Enregistrement...' : (isEdit ? 'Modifier' : 'Créer')}
                </Button>
              </Col>
            </Row>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default AddSupplier;